import React, { useState, useMemo } from "react";
import { Box, Typography } from "../../../design-system";
import logoSite from "../../../../assets/brand/logo_site.svg";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

/**
 * ET VIEWS LAB — an interactive planning sandbox on the home page (variant I).
 * Three concept layouts share ONE working clip engine (mock data, real filter/sort/pick/basket):
 *   • Prototype  — a faithful recreation of the Gemini-built UI (left filters | clips | right Basket, Source player, 3 view modes)
 *   • Council    — the council's recommended design (Table default, Basket as the hero, compact filters, Manual-clip drawer)
 *   • Fusion     — our current ET's dense toolbar + the Basket + view switching (best of both)
 * Every button works on mock data so we can feel each option before integrating into the real ET.
 */

const PINK = "#e61d50";
type Tone = "positive" | "neutral" | "negative";
type View = "list" | "table" | "gallery";
type Concept = "prototype" | "council" | "fusion";
interface Clip { id: number; text: string; speaker: string; tone: Tone; start: number; end: number; chars: number; manual?: boolean; }

const TONE_META: Record<Tone, { label: string; color: string }> = {
  positive: { label: "POSITIVE", color: "#22c55e" },
  neutral: { label: "NEUTRAL", color: "#4f8cff" },
  negative: { label: "NEGATIVE", color: "#ff5a5a" },
};

// The real transcript from the screenshots (Jonathan giving video-editing feedback).
const SRC: Omit<Clip, "id" | "chars">[] = [
  { text: "So every story has its cherries, pick yours.", speaker: "Jonathan", tone: "positive", start: 0, end: 2.29 },
  { text: "The future of video editing.", speaker: "Jonathan", tone: "neutral", start: 2.29, end: 4.0 },
  { text: "Number one is your hierarchy, I think is really good.", speaker: "Jonathan", tone: "positive", start: 4.0, end: 7.09 },
  { text: "Because the background is supporting the design, it's not fighting with the design.", speaker: "Jonathan", tone: "positive", start: 7.09, end: 11.5 },
  { text: "And your three sentences here also flow really well.", speaker: "Jonathan", tone: "positive", start: 11.5, end: 16.3 },
  { text: "The call to action is clear.", speaker: "Jonathan", tone: "positive", start: 16.3, end: 17.8 },
  { text: "The contrast is good.", speaker: "Jonathan", tone: "positive", start: 17.8, end: 19.3 },
  { text: "And this sentence pulls me in.", speaker: "Jonathan", tone: "positive", start: 19.3, end: 21.3 },
  { text: "The one thing I think you lost me on a little bit,", speaker: "Jonathan", tone: "neutral", start: 21.3, end: 24.1 },
  { text: "two things I'm going to point out,", speaker: "Jonathan", tone: "neutral", start: 24.1, end: 25.5 },
];
const MOCK: Clip[] = SRC.map((c, i) => ({ ...c, id: i + 1, chars: c.text.length }));
const SOURCE_LEN = 68.07; // matches the "01:08.07" master-reference duration in the screenshots

const pad = (n: number) => String(n).padStart(2, "0");
const tc = (s: number) => { const m = Math.floor(s / 60); const sec = Math.floor(s % 60); const cs = Math.round((s - Math.floor(s)) * 100); return `${pad(m)}:${pad(sec)}.${pad(cs)}`; };
const durOf = (c: Clip) => Math.max(0, Math.round(c.end - c.start));
const VIEWS: { key: View; Icon: React.ElementType; label: string }[] = [
  { key: "list", Icon: ViewAgendaIcon, label: "List" },
  { key: "table", Icon: TableRowsIcon, label: "Table" },
  { key: "gallery", Icon: GridViewIcon, label: "Gallery" },
];
const CONCEPTS: { key: Concept; label: string; blurb: string }[] = [
  { key: "prototype", label: "Prototype", blurb: "A faithful rebuild of your Gemini UI — filters on the left, clips in the middle (List / Table / Gallery), Basket on the right, and a Source player to hand-cut clips." },
  { key: "council", label: "Council's Pick", blurb: "The recommended design: Table stays the default (protects the “video-as-spreadsheet” identity), the Basket is the hero (it fixes the pick→export loop), filters go compact, manual-clipping tucks into a drawer." },
  { key: "fusion", label: "Fusion", blurb: "Best of both: keep our real ET’s dense labeled toolbar, bolt on the right-hand Basket, and add the List / Gallery view switch. The least disruptive path into the live editor." },
];

const EtLab: React.FC = () => {
  const [concept, setConcept] = useState<Concept>("prototype");
  const [view, setView] = useState<View>("gallery");
  const [clips, setClips] = useState<Clip[]>(MOCK);
  const [tones, setTones] = useState<Set<Tone>>(new Set());
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"chrono" | "alpha" | "duration" | "virality">("chrono");
  const [manualFilter, setManualFilter] = useState<"show" | "hide" | "only">("show");
  const [basket, setBasket] = useState<number[]>([]);
  const [cardOpts, setCardOpts] = useState({ timecodes: true, speaker: true, tone: true, thumbnails: true });
  const [playhead, setPlayhead] = useState(9.01);
  const [srcIn, setSrcIn] = useState<number | null>(null);
  const [srcOut, setSrcOut] = useState<number | null>(null);
  const [showSource, setShowSource] = useState(false); // council concept: Source lives in a drawer
  const [toast, setToast] = useState("");

  const say = (m: string) => { setToast(m); window.clearTimeout((say as any)._t); (say as any)._t = window.setTimeout(() => setToast(""), 3600); };

  // When the concept changes, snap to that concept's sensible default view.
  const pickConcept = (c: Concept) => { setConcept(c); setView(c === "prototype" ? "gallery" : "table"); };

  const visible = useMemo(() => {
    let out = clips.filter((c) =>
      (tones.size === 0 || tones.has(c.tone)) &&
      (!search.trim() || c.text.toLowerCase().includes(search.trim().toLowerCase())) &&
      (manualFilter === "show" || (manualFilter === "only" ? c.manual : !c.manual))
    );
    if (sort === "alpha") out = [...out].sort((a, b) => a.text.localeCompare(b.text));
    else if (sort === "duration") out = [...out].sort((a, b) => durOf(a) - durOf(b));
    else if (sort === "virality") out = [...out].sort((a, b) => (b.tone === "positive" ? 1 : 0) - (a.tone === "positive" ? 1 : 0) || a.id - b.id);
    else out = [...out].sort((a, b) => a.start - b.start);
    return out;
  }, [clips, tones, search, sort, manualFilter]);

  const inBasket = (id: number) => basket.includes(id);
  const togglePick = (id: number) => setBasket((b) => (b.includes(id) ? b.filter((x) => x !== id) : [...b, id]));
  const moveBasket = (id: number, dir: -1 | 1) => setBasket((b) => { const i = b.indexOf(id); const j = i + dir; if (i < 0 || j < 0 || j >= b.length) return b; const n = [...b]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const toggleTone = (t: Tone) => setTones((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const trim = (id: number, edge: "start" | "end", d: number) => setClips((cs) => cs.map((c) => { if (c.id !== id) return c; if (edge === "start") return { ...c, start: Math.max(0, Math.min(c.end - 0.5, c.start + d)) }; return { ...c, end: Math.max(c.start + 0.5, c.end + d) }; }));
  const reset = () => { setClips(MOCK); setTones(new Set()); setSearch(""); setSort("chrono"); setManualFilter("show"); setBasket([]); setSrcIn(null); setSrcOut(null); say("Reset — filters, picks & manual clips cleared."); };
  const clipIt = () => {
    const i = srcIn ?? playhead, o = srcOut ?? Math.min(SOURCE_LEN, (srcIn ?? playhead) + 3);
    const lo = Math.min(i, o), hi = Math.max(i, o);
    const id = Math.max(0, ...clips.map((c) => c.id)) + 1;
    const nc: Clip = { id, text: `Manual clip · ${tc(lo)}–${tc(hi)}`, speaker: "Jonathan", tone: "neutral", start: lo, end: hi, chars: 0, manual: true };
    setClips((cs) => [...cs, nc]); setBasket((b) => [...b, id]); setSrcIn(null); setSrcOut(null);
    say(`Clipped a manual clip (${tc(lo)}–${tc(hi)}) and dropped it in your Basket.`);
  };

  const basketClips = basket.map((id) => clips.find((c) => c.id === id)).filter(Boolean) as Clip[];
  const basketTotal = basketClips.reduce((s, c) => s + durOf(c), 0);

  // ── shared style helpers ─────────────────────────────────────────────
  const font = "Outfit, sans-serif";
  const chip = (active: boolean, color: string) => ({ cursor: "pointer", fontFamily: font, fontSize: "11.5px", fontWeight: 600, borderRadius: "7px", padding: "5px 10px", whiteSpace: "nowrap" as const, lineHeight: 1.3, border: `1px solid ${active ? color : "rgba(255,255,255,0.14)"}`, background: active ? color : "transparent", color: active ? "#0b0a0f" : "#c2c7d6", display: "inline-flex", alignItems: "center", gap: "5px" });
  const toneChip = (t: Tone) => { const m = TONE_META[t]; return { fontFamily: font, fontSize: "9.5px", fontWeight: 800, letterSpacing: "0.06em", borderRadius: "999px", padding: "3px 9px", color: "#fff", background: m.color }; };
  const stepBtn = { cursor: "pointer", width: 20, height: 20, borderRadius: "5px", border: "1px solid rgba(255,255,255,0.16)", background: "transparent", color: "#c2c7d6", display: "flex", alignItems: "center", justifyContent: "center", p: 0 } as const;
  const panel = { border: "1px solid rgba(255,255,255,0.10)", borderRadius: "14px", background: "#100e15" };
  const thumb = (c: Clip, w = 96, h = 54) => (
    <Box sx={{ width: w, height: h, flexShrink: 0, borderRadius: "7px", background: "linear-gradient(145deg,#1a1420,#0d0a12)", border: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
      <img src={logoSite} alt="" width={Math.round(w * 0.22)} style={{ opacity: 0.8 }} />
      <Box component="span" sx={{ position: "absolute", bottom: 3, left: 0, right: 0, textAlign: "center", fontFamily: font, fontSize: "8px", fontWeight: 700, color: "#fff", textShadow: "0 1px 3px #000", px: "2px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{c.text.split(" ").slice(0, 2).join(" ")}</Box>
    </Box>
  );

  // ── in/out stepper (used by table + list + gallery) ──────────────────
  const inOut = (c: Clip, compact = false) => (
    <Box sx={{ display: "flex", flexDirection: compact ? "row" : "column", gap: compact ? "10px" : "4px" }}>
      {(["start", "end"] as const).map((edge) => (
        <Box key={edge} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <Typography sx={{ fontFamily: font, fontSize: "8px", color: "#6b6675", width: 20 }}>{edge === "start" ? "IN" : "OUT"}</Typography>
          <Box component="button" onClick={() => trim(c.id, edge, -0.5)} sx={stepBtn}><RemoveIcon sx={{ fontSize: 12 }} /></Box>
          <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "11.5px", color: edge === "start" ? "#e8e9ef" : "#f5a524", minWidth: 62, textAlign: "center" }}>{tc(edge === "start" ? c.start : c.end)}</Typography>
          <Box component="button" onClick={() => trim(c.id, edge, 0.5)} sx={stepBtn}><AddIcon sx={{ fontSize: 12 }} /></Box>
        </Box>
      ))}
    </Box>
  );

  const pickDot = (c: Clip) => {
    const order = basket.indexOf(c.id);
    const on = order >= 0;
    return (
      <Box component="button" onClick={() => togglePick(c.id)} title="Add to Basket" sx={{ cursor: "pointer", flexShrink: 0, width: 26, height: 26, borderRadius: "50%", border: `1.5px solid ${on ? PINK : "rgba(255,255,255,0.3)"}`, background: on ? PINK : "transparent", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: font, fontSize: "11px", fontWeight: 800, p: 0 }}>
        {on ? order + 1 : ""}
      </Box>
    );
  };

  // ── the three view renderers (share cardOpts + pick state) ────────────
  const clipRow = (c: Clip) => {
    const on = inBasket(c.id);
    return (
      <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: "16px", p: "14px 16px", borderRadius: "12px", border: `1px solid ${on ? "rgba(230,29,80,0.5)" : "rgba(255,255,255,0.07)"}`, background: on ? "rgba(230,29,80,0.05)" : "rgba(255,255,255,0.015)", mb: "10px" }}>
        {pickDot(c)}
        {cardOpts.thumbnails && thumb(c, 132, 74)}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "17px", color: "#f3f4f9", lineHeight: 1.3 }}>{c.text}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "8px", flexWrap: "wrap" }}>
            {cardOpts.speaker && <Typography sx={{ fontFamily: font, fontSize: "11px", fontWeight: 700, color: "#8d94a8", letterSpacing: "0.04em" }}>{c.speaker.toUpperCase()}</Typography>}
            {cardOpts.tone && <Box component="span" sx={toneChip(c.tone)}>{TONE_META[c.tone].label}</Box>}
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: "#6b6675" }}>⏱ {durOf(c)}s · {c.chars} ch</Typography>
            {c.manual && <Box component="span" sx={{ ...toneChip("neutral"), background: "#8b5cf6" }}>MANUAL</Box>}
          </Box>
        </Box>
        {cardOpts.timecodes && inOut(c, true)}
      </Box>
    );
  };

  const galleryCard = (c: Clip) => {
    const on = inBasket(c.id);
    return (
      <Box key={c.id} sx={{ borderRadius: "12px", border: `1px solid ${on ? "rgba(230,29,80,0.55)" : "rgba(255,255,255,0.08)"}`, background: on ? "rgba(230,29,80,0.05)" : "rgba(255,255,255,0.02)", p: "10px", position: "relative" }}>
        <Box sx={{ position: "absolute", top: 16, left: 16, zIndex: 2 }}>{pickDot(c)}</Box>
        {cardOpts.thumbnails && <Box sx={{ mb: "9px" }}>{thumb(c, "100%" as any, 108)}</Box>}
        <Typography sx={{ fontFamily: font, fontWeight: 600, fontSize: "13.5px", color: "#f3f4f9", lineHeight: 1.35, minHeight: "37px" }}>{c.text}</Typography>
        {cardOpts.timecodes && <Box sx={{ mt: "8px" }}>{inOut(c)}</Box>}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "9px", flexWrap: "wrap" }}>
          {cardOpts.speaker && <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 700, color: "#8d94a8" }}>{c.speaker}</Typography>}
          {cardOpts.tone && <Box component="span" sx={{ ...toneChip(c.tone), fontSize: "8.5px" }}>{TONE_META[c.tone].label}</Box>}
          <Typography sx={{ fontFamily: font, fontSize: "10px", color: "#6b6675" }}>{durOf(c)}s</Typography>
          <Box sx={{ flex: 1 }} />
          <Box component="button" onClick={() => say("Download (demo): saves this single clip.")} sx={{ cursor: "pointer", background: "transparent", border: "none", color: "#8d94a8", p: 0, display: "flex" }}><DownloadIcon sx={{ fontSize: 16 }} /></Box>
        </Box>
      </Box>
    );
  };

  const th = { textAlign: "left" as const, padding: "9px 12px", fontFamily: font, fontWeight: 700, fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase" as const, color: "#8d94a8", borderBottom: "1px solid rgba(255,255,255,0.1)", whiteSpace: "nowrap" as const };
  const tdd = { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", fontFamily: font, fontSize: "13.5px", verticalAlign: "middle" as const };
  const tableView = () => (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: "760px" }}>
        <Box component="thead"><Box component="tr">
          <Box component="th" sx={th} />
          {cardOpts.thumbnails && <Box component="th" sx={th}>Thumbnail</Box>}
          <Box component="th" sx={th}>Transcript / Content</Box>
          {cardOpts.speaker && <Box component="th" sx={th}>Speaker</Box>}
          {cardOpts.timecodes && <Box component="th" sx={th}>Timecode</Box>}
          <Box component="th" sx={th}>Dur</Box>
          <Box component="th" sx={th}>Chars</Box>
          {cardOpts.tone && <Box component="th" sx={th}>Tone</Box>}
          <Box component="th" sx={th} />
        </Box></Box>
        <Box component="tbody">
          {visible.map((c) => {
            const on = inBasket(c.id);
            return (
              <Box component="tr" key={c.id} sx={{ background: on ? "rgba(230,29,80,0.06)" : "transparent", "&:hover": { background: "rgba(255,255,255,0.02)" } }}>
                <Box component="td" sx={{ ...tdd, textAlign: "center" }}>{pickDot(c)}</Box>
                {cardOpts.thumbnails && <Box component="td" sx={tdd}>{thumb(c)}</Box>}
                <Box component="td" sx={{ ...tdd, color: "#f3f4f9", minWidth: 240, maxWidth: 380 }}>{c.text}{c.manual && <Box component="span" sx={{ ...toneChip("neutral"), background: "#8b5cf6", ml: "8px" }}>MANUAL</Box>}</Box>
                {cardOpts.speaker && <Box component="td" sx={{ ...tdd, color: "#8d94a8" }}>{c.speaker}</Box>}
                {cardOpts.timecodes && <Box component="td" sx={tdd}>{inOut(c)}</Box>}
                <Box component="td" sx={{ ...tdd, color: "#c2c7d6", fontWeight: 700 }}>{durOf(c)}s</Box>
                <Box component="td" sx={{ ...tdd, color: "#8d94a8" }}>{c.chars}</Box>
                {cardOpts.tone && <Box component="td" sx={tdd}><Box component="span" sx={toneChip(c.tone)}>{TONE_META[c.tone].label}</Box></Box>}
                <Box component="td" sx={{ ...tdd, textAlign: "right" }}><Box component="button" onClick={() => say("Download (demo): saves this single clip.")} sx={{ cursor: "pointer", background: "transparent", border: "none", color: "#8d94a8", p: 0 }}><DownloadIcon sx={{ fontSize: 17 }} /></Box></Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  const clipArea = () => {
    if (visible.length === 0) return <Box sx={{ p: "40px", textAlign: "center", fontFamily: font, color: "#8d94a8", fontSize: "14px" }}>No clips match. <Box component="button" onClick={reset} sx={{ ...chip(false, PINK), ml: "8px", color: PINK }}>Reset</Box></Box>;
    if (view === "table") return tableView();
    if (view === "gallery") return <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3,1fr)", lg: concept === "prototype" ? "repeat(3,1fr)" : "repeat(4,1fr)" }, gap: "12px" }}>{visible.map(galleryCard)}</Box>;
    return <Box>{visible.map(clipRow)}</Box>;
  };

  const viewToggle = () => (
    <Box sx={{ display: "inline-flex", gap: "2px", p: "3px", borderRadius: "9px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
      {VIEWS.map((v) => { const a = view === v.key; return (
        <Box key={v.key} component="button" onClick={() => setView(v.key)} title={v.label} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", border: "none", borderRadius: "7px", p: "5px 9px", background: a ? PINK : "transparent", color: a ? "#fff" : "#8d94a8", fontFamily: font, fontSize: "11px", fontWeight: 600 }}>
          <v.Icon sx={{ fontSize: 16 }} />{v.label}
        </Box>
      ); })}
    </Box>
  );

  // ── the Basket (right rail) ──────────────────────────────────────────
  const basketPanel = (hero = false) => (
    <Box sx={{ ...panel, display: "flex", flexDirection: "column", ...(hero ? { border: `1px solid ${PINK}55`, boxShadow: `0 0 0 1px ${PINK}22, 0 20px 60px -30px ${PINK}66` } : {}) }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", p: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <ShoppingBasketIcon sx={{ fontSize: 18, color: PINK }} />
        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "14px", color: "#f3f4f9" }}>Basket</Typography>
        {hero && <Box component="span" sx={{ ...toneChip("neutral"), background: PINK, fontSize: "8px" }}>★ THE UNLOCK</Box>}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", color: "#f5a524" }}>{tc(basketTotal)}</Typography>
      </Box>
      <Box sx={{ p: "12px 14px", flex: 1, minHeight: 120, maxHeight: 420, overflowY: "auto" }}>
        {basketClips.length === 0 ? (
          <Box sx={{ textAlign: "center", py: "28px" }}>
            <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#8d94a8" }}>Your picked clips land here — in order.</Typography>
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: "#5b5364", mt: "5px" }}>Reorder them here, not in the list. This is the fix for “plays forever / can’t pause.”</Typography>
          </Box>
        ) : basketClips.map((c, i) => (
          <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "8px", p: "8px", borderRadius: "9px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "12px", color: PINK, width: 16 }}>{i + 1}</Typography>
            {thumb(c, 52, 30)}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: "#e8e9ef", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{c.text}</Typography>
              <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "9.5px", color: "#6b6675" }}>{tc(c.start)}–{tc(c.end)} · {durOf(c)}s</Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box component="button" onClick={() => moveBasket(c.id, -1)} sx={{ ...stepBtn, width: 18, height: 15, border: "none" }}><KeyboardArrowUpIcon sx={{ fontSize: 14 }} /></Box>
              <Box component="button" onClick={() => moveBasket(c.id, 1)} sx={{ ...stepBtn, width: 18, height: 15, border: "none" }}><KeyboardArrowDownIcon sx={{ fontSize: 14 }} /></Box>
            </Box>
            <Box component="button" onClick={() => togglePick(c.id)} sx={{ ...stepBtn, width: 20, height: 20, border: "none", color: "#8d94a8" }}><CloseIcon sx={{ fontSize: 14 }} /></Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ p: "12px 14px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <Box component="button" onClick={() => say(basketClips.length ? `Exporting ${basketClips.length} clips stitched into one video (${tc(basketTotal)}) — demo.` : "Pick some clips first.")} sx={{ width: "100%", cursor: "pointer", border: "none", borderRadius: "10px", p: "10px", fontFamily: font, fontWeight: 800, fontSize: "13.5px", color: "#fff", background: "linear-gradient(100deg,#ff5c8a,#ff2e63)", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}><PlayArrowIcon sx={{ fontSize: 18 }} />Stitch → one video</Box>
        <Box sx={{ display: "flex", gap: "6px", mt: "8px" }}>
          {["MP4", "GIF", "SRT"].map((f) => <Box key={f} component="button" onClick={() => say(`Export ${f} (demo).`)} sx={{ ...chip(false, "#8d94a8"), flex: 1, justifyContent: "center", color: "#c2c7d6" }}><DownloadIcon sx={{ fontSize: 13 }} />{f}</Box>)}
        </Box>
      </Box>
    </Box>
  );

  // ── the Source player (Master Reference) ─────────────────────────────
  const sourcePlayer = () => (
    <Box sx={{ ...panel, p: "14px 16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}>
        <Box component="span" sx={{ ...toneChip("neutral"), background: PINK }}>SOURCE</Box>
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "13px", color: "#f3f4f9" }}>Master reference — cut your own clip</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "11px", color: "#8d94a8" }}>{tc(playhead)} / {tc(SOURCE_LEN)}</Typography>
      </Box>
      <Box sx={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "stretch" }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Box sx={{ position: "relative", borderRadius: "10px", overflow: "hidden", background: "linear-gradient(145deg,#1a1420,#0d0a12)", border: "1px solid rgba(255,255,255,0.08)", height: 150, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={logoSite} alt="" width={40} style={{ opacity: 0.7 }} />
            <Typography sx={{ position: "absolute", bottom: 10, fontFamily: font, fontWeight: 800, color: "#fff", textShadow: "0 1px 4px #000", fontSize: "15px" }}>Every story has its cherries…</Typography>
          </Box>
          <input type="range" min={0} max={SOURCE_LEN} step={0.01} value={playhead} onChange={(e) => setPlayhead(Number(e.target.value))} style={{ width: "100%", accentColor: PINK, marginTop: 10 }} />
          <Box sx={{ display: "flex", gap: "6px", mt: "4px" }}>
            {["-5s", "-1s", "Play", "+1s", "+5s"].map((b) => <Box key={b} component="button" onClick={() => { if (b === "Play") say("Play (demo): the full uncut video would play here."); else setPlayhead((p) => Math.max(0, Math.min(SOURCE_LEN, p + Number(b.replace("s", ""))))); }} sx={{ ...chip(b === "Play", b === "Play" ? "#fff" : "#8d94a8"), color: b === "Play" ? "#0b0a0f" : "#c2c7d6" }}>{b === "Play" ? <><PlayArrowIcon sx={{ fontSize: 14 }} />Play</> : b}</Box>)}
          </Box>
        </Box>
        <Box sx={{ width: 200, display: "flex", flexDirection: "column", gap: "8px" }}>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Box component="button" onClick={() => setSrcIn(playhead)} sx={{ flex: 1, cursor: "pointer", borderRadius: "9px", border: "1px solid #22c55e77", background: "rgba(34,197,94,0.08)", p: "9px 6px", fontFamily: font }}>
              <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 800, color: "#22c55e" }}>SET IN</Typography>
              <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", color: "#e8e9ef" }}>{srcIn == null ? "––:––" : tc(srcIn)}</Typography>
            </Box>
            <Box component="button" onClick={() => setSrcOut(playhead)} sx={{ flex: 1, cursor: "pointer", borderRadius: "9px", border: "1px solid #f5a52477", background: "rgba(245,165,36,0.08)", p: "9px 6px", fontFamily: font }}>
              <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 800, color: "#f5a524" }}>SET OUT</Typography>
              <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", color: "#e8e9ef" }}>{srcOut == null ? "––:––" : tc(srcOut)}</Typography>
            </Box>
          </Box>
          <Box component="button" onClick={clipIt} sx={{ cursor: "pointer", border: "none", borderRadius: "10px", p: "11px", fontFamily: font, fontWeight: 800, fontSize: "14px", color: "#fff", background: PINK, display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}><ContentCutIcon sx={{ fontSize: 17 }} />CLIP IT</Box>
          <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: "#5b5364", lineHeight: 1.5 }}>Scrub, set In & Out, then Clip It — a Manual Clip drops into your Basket and the list.</Typography>
        </Box>
      </Box>
    </Box>
  );

  const cardOptsPanel = () => (
    <Box sx={{ ...panel, p: "12px 14px" }}>
      <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#8d94a8", mb: "10px" }}>Card view options</Typography>
      {([["timecodes", "Timecodes"], ["speaker", "Speaker"], ["tone", "Tone colors"], ["thumbnails", "Thumbnails"]] as const).map(([k, label]) => (
        <Box key={k} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "9px" }}>
          <Typography sx={{ fontFamily: font, fontSize: "13px", color: "#e8e9ef" }}>{label}</Typography>
          <Box component="button" onClick={() => setCardOpts((o) => ({ ...o, [k]: !o[k] }))} sx={{ cursor: "pointer", width: 38, height: 22, borderRadius: "999px", border: "none", p: 0, background: cardOpts[k] ? PINK : "rgba(255,255,255,0.14)", position: "relative", transition: "background .15s" }}>
            <Box sx={{ position: "absolute", top: 2, left: cardOpts[k] ? 18 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  // ── filters ──────────────────────────────────────────────────────────
  const sortChips = () => (["chrono", "alpha", "duration", "virality"] as const).map((s) => (
    <Box key={s} component="button" onClick={() => setSort(s)} sx={chip(sort === s, "#6aa8ff")}>{s === "chrono" ? "Chrono" : s === "alpha" ? "A–Z" : s === "duration" ? "Duration" : "Virality"}</Box>
  ));
  const toneChips = () => (["positive", "neutral", "negative"] as const).map((t) => (
    <Box key={t} component="button" onClick={() => toggleTone(t)} sx={chip(tones.has(t), TONE_META[t].color)}>{TONE_META[t].label}</Box>
  ));
  const manualChips = () => (["show", "hide", "only"] as const).map((m) => (
    <Box key={m} component="button" onClick={() => setManualFilter(m)} sx={chip(manualFilter === m, "#8b5cf6")}>{m[0].toUpperCase() + m.slice(1)}</Box>
  ));
  const searchField = (w: string | number = "100%") => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.04)", px: "9px", width: w }}>
      <SearchIcon sx={{ fontSize: 15, color: "#8d94a8" }} />
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search transcripts…" style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "#f3f4f9", fontFamily: font, fontSize: "12.5px", padding: "8px 0" }} />
    </Box>
  );

  const filterRail = () => (
    <Box sx={{ ...panel, p: "14px", display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}><FilterAltIcon sx={{ fontSize: 17, color: "#8d94a8" }} /><Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "13px", color: "#f3f4f9" }}>Filters</Typography></Box>
      {searchField()}
      <Box><Typography sx={lblSx}>Sort order</Typography><Box sx={rowWrap}>{sortChips()}</Box></Box>
      <Box><Typography sx={lblSx}>Tone & emotion</Typography><Box sx={rowWrap}>{toneChips()}</Box></Box>
      <Box><Typography sx={lblSx}>Speakers</Typography><Box sx={rowWrap}><Box component="button" sx={chip(true, "#f5a524")}>● Jonathan</Box></Box></Box>
      <Box><Typography sx={lblSx}>Manual clips</Typography><Box sx={rowWrap}>{manualChips()}</Box></Box>
      <Box component="button" onClick={reset} sx={{ ...chip(false, PINK), color: PINK, justifyContent: "center" }}>Reset all</Box>
    </Box>
  );

  const lblSx = { fontFamily: font, fontWeight: 700, fontSize: "9.5px", letterSpacing: "0.12em", textTransform: "uppercase" as const, color: "#6b6675", mb: "8px", display: "block" };
  const rowWrap = { display: "flex", flexWrap: "wrap" as const, gap: "6px" };

  // ── the dense ET toolbar (fusion concept) ────────────────────────────
  const denseToolbar = () => (
    <Box sx={{ ...panel, display: "flex", alignItems: "center", gap: "10px", p: "9px 12px", overflowX: "auto" }}>
      <Box sx={{ flexShrink: 0 }}><Typography sx={{ fontFamily: font, fontSize: "9px", color: "#6b6675", textTransform: "uppercase", letterSpacing: "0.1em" }}>Clips <b style={{ color: "#f3f4f9" }}>{visible.length}</b></Typography><Typography sx={{ fontFamily: font, fontSize: "9px", color: "#6b6675", textTransform: "uppercase", letterSpacing: "0.1em" }}>Basket <b style={{ color: PINK }}>{basket.length}</b></Typography></Box>
      <Box sx={{ width: "1px", alignSelf: "stretch", background: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ ...rowWrap, flexShrink: 0 }}>{toneChips()}</Box>
      <Box sx={{ width: "1px", alignSelf: "stretch", background: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ flexShrink: 0 }}>{searchField(160)}</Box>
      <Box sx={{ ...rowWrap, flexShrink: 0 }}>{sortChips()}</Box>
      <Box sx={{ width: "1px", alignSelf: "stretch", background: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ ...rowWrap, flexShrink: 0 }}>{manualChips()}</Box>
      <Box component="button" onClick={reset} sx={{ ...chip(false, "#ff5a5a"), color: "#ff5a5a", flexShrink: 0 }}>Reset</Box>
    </Box>
  );

  const clipStage = (extraTopRight?: React.ReactNode) => (
    <Box sx={{ ...panel, p: "12px 14px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "12px", flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 800, fontSize: "13px", color: "#f3f4f9" }}>{visible.length} clips</Typography>
        <Box sx={{ flex: 1 }} />
        {extraTopRight}
        {viewToggle()}
      </Box>
      {clipArea()}
    </Box>
  );

  // ── concept layouts ──────────────────────────────────────────────────
  const layout = () => {
    if (concept === "prototype") {
      return (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "232px 1fr 312px" }, gap: "14px", alignItems: "start" }}>
          <Box sx={{ display: { xs: "none", lg: "block" } }}>{filterRail()}</Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 0 }}>
            {sourcePlayer()}
            {clipStage(<Box sx={{ display: { xs: "none", md: "block" } }} />)}
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", position: { lg: "sticky" }, top: { lg: "88px" } }}>{cardOptsPanel()}{basketPanel()}</Box>
        </Box>
      );
    }
    if (concept === "council") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <Box sx={{ ...panel, p: "11px 14px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {searchField(200)}
            <Box sx={rowWrap}>{toneChips()}</Box>
            <Box sx={rowWrap}>{sortChips()}</Box>
            <Box sx={{ flex: 1 }} />
            <Box component="button" onClick={() => setShowSource((v) => !v)} sx={chip(showSource, "#8b5cf6")}><ContentCutIcon sx={{ fontSize: 14 }} />Manual clip</Box>
            <Box component="button" onClick={reset} sx={{ ...chip(false, "#ff5a5a"), color: "#ff5a5a" }}>Reset</Box>
          </Box>
          {showSource && sourcePlayer()}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 330px" }, gap: "14px", alignItems: "start" }}>
            {clipStage()}
            <Box sx={{ position: { lg: "sticky" }, top: { lg: "88px" } }}>{basketPanel(true)}</Box>
          </Box>
        </Box>
      );
    }
    // fusion
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {denseToolbar()}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" }, gap: "14px", alignItems: "start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", minWidth: 0 }}>
            {clipStage(<Box component="button" onClick={() => setShowSource((v) => !v)} sx={chip(showSource, "#8b5cf6")}><ContentCutIcon sx={{ fontSize: 14 }} />Source</Box>)}
            {showSource && sourcePlayer()}
          </Box>
          <Box sx={{ position: { lg: "sticky" }, top: { lg: "88px" } }}>{basketPanel()}</Box>
        </Box>
      </Box>
    );
  };

  const activeConcept = CONCEPTS.find((c) => c.key === concept)!;

  return (
    <Box sx={{ fontFamily: font }}>
      {/* concept switch + intro */}
      <Box sx={{ mb: "18px" }}>
        <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: "11px", letterSpacing: "0.16em", textTransform: "uppercase", color: PINK, mb: "6px" }}>ET Views Lab — pick a concept, then a view</Typography>
        <Box sx={{ display: "inline-flex", gap: "4px", p: "4px", borderRadius: "12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", flexWrap: "wrap" }}>
          {CONCEPTS.map((c) => { const a = concept === c.key; return (
            <Box key={c.key} component="button" onClick={() => pickConcept(c.key)} sx={{ cursor: "pointer", border: "none", borderRadius: "9px", p: "9px 16px", background: a ? PINK : "transparent", color: a ? "#fff" : "#c2c7d6", fontFamily: font, fontSize: "14px", fontWeight: 700 }}>{c.label}</Box>
          ); })}
        </Box>
        <Typography sx={{ fontFamily: font, fontSize: "14px", color: "#9aa0b4", lineHeight: 1.6, mt: "12px", maxWidth: 860 }}>{activeConcept.blurb}</Typography>
      </Box>

      {layout()}

      {toast && <Box sx={{ position: "fixed", bottom: 84, left: "50%", transform: "translateX(-50%)", zIndex: 12500, px: "16px", py: "10px", borderRadius: "10px", background: "rgba(20,16,25,0.96)", border: `1px solid ${PINK}55`, color: "#f3f4f9", fontFamily: font, fontSize: "13px", boxShadow: "0 20px 50px -20px rgba(0,0,0,0.8)" }}>💡 {toast}</Box>}
    </Box>
  );
};

const EtLabPage: React.FC = () => (
  <Box sx={{ maxWidth: 1560, mx: "auto", px: { xs: "16px", md: "28px" }, py: { xs: "28px", md: "44px" } }}>
    <Typography sx={{ fontFamily: "'Anton',Outfit,sans-serif", fontSize: { xs: "34px", md: "52px" }, color: "#fff", lineHeight: 1.05, letterSpacing: "0.01em" }}>The ET, three ways.</Typography>
    <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: { xs: "15px", md: "17px" }, color: "#9aa0b4", lineHeight: 1.6, mt: "12px", maxWidth: 720 }}>
      A working sandbox to feel each direction before we build it into the real editor. Same clips, same picks — three layouts, three view modes (List / Table / Gallery), a Basket, and a Source player for hand-cut clips. Everything's clickable.
    </Typography>
    <Box sx={{ mt: "28px" }}><EtLab /></Box>
  </Box>
);

export default EtLabPage;
